import assert, { AssertionError } from 'assert';
import { match, P } from 'ts-pattern';

import type { SafeString } from '@fragment/types/strings';
import { SafeStringSchema } from '@fragment/types/strings';

export type ConstantComponent = {
  _const: true;
  shorthand: string;
  value: string;
};

export type Component<T> =
  | [keyof T, string]
  | [keyof T, string, boolean]
  | [keyof T, string, boolean, string]
  | [keyof T, string, boolean, string | undefined, boolean]
  | ConstantComponent;

const isConstantComponent = <T>(
  component: Component<T>
): component is ConstantComponent =>
  !Array.isArray(component) && component._const === true;

export type SafeIdEncoderInput = { [key: string]: string | number };

type ExcludeNumber<T extends SafeIdEncoderInput> = {
  [K in keyof T]: Exclude<T[K], number>;
};
/**
 * This doens't enforce that the config you pass in also uses SafeString
 * to save on the eyesore of having to use safe`workspaceId` everywhere.
 * So don't use keys or shorthands that are not SafeStrings.
 */
export class SafeIdEncoder<T extends SafeIdEncoderInput = {}> {
  private config: Array<Component<T>> = [];

  constructor(config?: Array<Component<T>>) {
    this.config = config ?? [];
    // validate that there are no duplicate keys (dynamic) or shorthands (constant + dynamic)
    const seenKeys = new Set();
    const seenShorthands = new Set<string>();
    config?.forEach((component) => {
      if (isConstantComponent(component)) {
        if (seenShorthands.has(component.shorthand)) {
          throw new AssertionError({
            message: `IdEncoder encountered duplicate shorthand '${component.shorthand}' (constant component)`,
          });
        }
        seenShorthands.add(component.shorthand);
        return;
      }
      const [key, shorthand, isOptional, defaultValue] = component;
      if (seenKeys.has(key)) {
        throw new AssertionError({
          message: `IdEncoder encountered duplicate key ${key.toString()}`,
        });
      }
      if (seenShorthands.has(shorthand)) {
        throw new AssertionError({
          message: `IdEncoder encountered duplicate shorthand '${shorthand}'`,
        });
      }
      if (!isOptional && defaultValue !== undefined) {
        throw new AssertionError({
          message: `IdEncoder encountered defaultValue for non-optional key '${key.toString()}'`,
        });
      }
      if (
        isOptional &&
        defaultValue !== undefined &&
        typeof defaultValue !== 'string'
      ) {
        throw new AssertionError({
          message: `IdEncoder encountered non-string defaultValue for optional key ${key.toString()}. Value was '${defaultValue}'`,
        });
      }
      seenKeys.add(key);
      seenShorthands.add(shorthand);
    });
  }

  public encode(data: T): string {
    return this.config
      .flatMap((component) => {
        if (isConstantComponent(component)) {
          return [`${component.shorthand}:${component.value}`];
        }
        const [key, shorthand, isOptional, , skipParse] = component;
        const value = data[key];
        if (isOptional && value === undefined) {
          return [];
        }

        // Skip SafeString validation if skipParse is true
        if (skipParse) {
          return [`${shorthand}:${value}`];
        }

        const result = SafeStringSchema.safeParse(value.toString());
        assert(
          result.success,
          `Unsafe value for key '${key.toString()}': '${value}'`
        );
        return [`${shorthand}:${result.data}`];
      })
      .join(':');
  }

  public decode(encoded: string): ExcludeNumber<T> {
    const components = new Map<string, string>(
      encoded
        .split(':')
        .reduce<{ final: [string, string][]; partial: string | undefined }>(
          (
            acc: { final: [string, string][]; partial: string | undefined },
            curr
          ) => {
            if (acc.partial === undefined) {
              return { final: acc.final, partial: curr };
            }
            return {
              final: [...acc.final, [acc.partial, curr]],
              partial: undefined,
            };
          },
          { final: [], partial: undefined }
        ).final
    );
    return this.config.reduce((acc, component) => {
      if (isConstantComponent(component)) {
        const observed = components.get(component.shorthand);
        assert(
          observed === component.value,
          `Constant component mismatch: expected '${component.shorthand}:${component.value}', got '${component.shorthand}:${observed}'`
        );
        return acc;
      }
      const [key, shorthand, isOptional, defaultValue, skipParse] = component;
      const value = components.get(shorthand);
      if (value === undefined && isOptional && !defaultValue) {
        return { ...acc, [key]: undefined };
      }
      if (value === undefined && isOptional && defaultValue !== undefined) {
        return { ...acc, [key]: defaultValue };
      }

      // Skip SafeString validation if skipParse is true
      if (skipParse) {
        return {
          ...acc,
          [key]: value,
        };
      }

      const result = SafeStringSchema.safeParse(value?.toString());
      assert(
        result.success,
        `Unsafe value for key '${key.toString()}': '${value}'`
      );
      return {
        ...acc,
        [key]: result.data,
      };
    }, {} as ExcludeNumber<T>);
  }

  public withComponent<TKey extends string, TValue extends string>(
    key: TKey,
    value: TValue,
    isOptional?: boolean,
    defaultValue?: string,
    skipParse?: boolean
  ): SafeIdEncoder<T & { [k in TKey]: SafeString | number }> {
    const component = match({ isOptional, defaultValue, skipParse })
      .returnType<Component<{ [k in TKey]: SafeString | number }>>()
      .with(
        {
          isOptional: P.nullish,
          defaultValue: P.nullish,
          skipParse: P.nullish,
        },
        () => [key, value]
      )
      .with(
        {
          isOptional: P.boolean,
          defaultValue: P.nullish,
          skipParse: P.nullish,
        },
        ({ isOptional: isOptionalMatch }) => [key, value, isOptionalMatch]
      )
      .with(
        {
          isOptional: P.boolean,
          defaultValue: P.nullish,
          skipParse: P.boolean,
        },
        ({ isOptional: isOptionalMatch, skipParse: skipParseMatch }) => [
          key,
          value,
          isOptionalMatch,
          undefined,
          skipParseMatch,
        ]
      )
      .with(
        { isOptional: true, defaultValue: P.string, skipParse: P.nullish },
        ({ defaultValue: defaultValueMatch }) => [
          key,
          value,
          true,
          defaultValueMatch,
        ]
      )
      .with(
        { isOptional: true, defaultValue: P.string, skipParse: P.boolean },
        ({ defaultValue: defaultValueMatch, skipParse: skipParseMatch }) => [
          key,
          value,
          true,
          defaultValueMatch,
          skipParseMatch,
        ]
      )
      .otherwise(
        ({ defaultValue: defaultValueMatch, isOptional: isOptionalMatch }) => {
          throw new AssertionError({
            message: `Invalid configuration for key '${key.toString()}. 
          If default value provided, isOptional must be true. 
          Default value: '${defaultValueMatch}'. 
          IsOptional: '${isOptionalMatch}'`,
          });
        }
      );
    return new SafeIdEncoder<T & { [k in TKey]: SafeString | number }>([
      ...this.config,
      component,
    ]);
  }

  /**
   * Append a fixed `shorthand:value` segment to the encoded output. Unlike
   * `withComponent`, this does not widen the encoder's input/output type — the
   * value is supplied at builder time and is never expected on the data
   * argument. Useful for entity-name discriminators baked into a partition key
   * (e.g. `entity:AuthPolicy`).
   *
   * `decode` asserts that the encoded string contains the expected
   * `shorthand:value` pair at this position; mismatches throw.
   */
  public withConstant(shorthand: string, value: string): SafeIdEncoder<T> {
    const parsed = SafeStringSchema.safeParse(value);
    assert(
      parsed.success,
      `withConstant: unsafe constant value for shorthand '${shorthand}': '${value}'`
    );
    const parsedShorthand = SafeStringSchema.safeParse(shorthand);
    assert(
      parsedShorthand.success,
      `withConstant: unsafe shorthand '${shorthand}'`
    );
    return new SafeIdEncoder<T>([
      ...this.config,
      { _const: true, shorthand, value },
    ]);
  }
}
